import { Injectable } from '@angular/core';
import { OneToOneSchedule, OneToOneScheduleDetailsView, OneToOneScheduleMinimal } from '../model/one-to-one-schedule.model';
import { OneToOneMessages } from '../locale/one-to-one.messages';
import { OneToOneAPIService } from '../api/one-to-one-api.service';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { Observable, firstValueFrom } from 'rxjs';
import { TalkingPointTemplate } from '../model/talking-point-template.model';
import { CreateOneToOneSchedule } from '../model/create-one-to-one-schedule.model';
import { TransferOneToOneSchedule } from '../model/transfer-one-to-one-schedule.model';
import { ParentFilter } from '@app/models/api/parent-filter.model';
import { Page } from '@app/models/api/page.model';
import { PagingParams } from '@app/models/api/paging-params.model';
import { SortingParams } from '@app/models/api/sorting-params.model';
import { OneToOneMeeting } from '../model/one-to-one-meeting.model';
import { TalkingPoint } from '../model/talking-point.model';
import { OneToOneNoteMinimal } from '../model/one-to-one-note.model';
import { OneToOneMeetingFile } from '../model/one-to-one-meeting-file.model';
import { RescheduleOneToOneMeeting } from '../model/reschedule-one-to-one-meeting.model';
import { OneToOneMeetingStatus } from '../model/one-to-one-meeting-status.model';
import { CreateMeetingSentimentScaleScore, MeetingSentimentScaleScore } from '../model/meeting-sentiment-scale-score.model';
import { ScheduleSentimentScaleTimeline } from '@app/shared/components/information-sidebar/tabs/information-sidebar-sentiment-scale-timeline/information-sidebar-sentiment-scale-timeline.component';
import { InFlightRegistry } from '@app/shared/utils/in-flight-registry';

@Injectable({
  providedIn: 'root'
})
export class OneToOneBusinessService {

  // key -> the write currently in flight for that schedule / meeting. A duplicate call while one is
  // in flight is coalesced onto the same promise (see InFlightRegistry) rather than rejected.
  private static _updatingSchedules: InFlightRegistry;
  private static _updatingMeetings: InFlightRegistry;

  constructor(
    private oneToOneAPIService: OneToOneAPIService,
    private notifyUtils: NotifyUtils
  ) {
    OneToOneBusinessService._updatingSchedules = new InFlightRegistry();
    OneToOneBusinessService._updatingMeetings = new InFlightRegistry();
  }

  public isModifyingSchedule(scheduleId: number): boolean {
    return OneToOneBusinessService._updatingSchedules.has(scheduleId);
  }

  public isModifyingMeeting(meetingId: number): boolean {
    return OneToOneBusinessService._updatingMeetings.has(meetingId);
  }

  createSchedule(createDto: CreateOneToOneSchedule): Promise<OneToOneSchedule> {
    return firstValueFrom(this.oneToOneAPIService.createOneToOneSchedule(createDto))
      .then(res => {
        this.notifyUtils.notify(OneToOneMessages.CREATE_SUCCESS_TOAST);
        return res;
      });
  }

  updateSchedule(id: number, updateDto: CreateOneToOneSchedule): Promise<OneToOneSchedule> {
    return OneToOneBusinessService._updatingSchedules.run(id, () =>
      firstValueFrom(this.oneToOneAPIService.updateOneToOneSchedule(id, updateDto))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.EDIT_SUCCESS_TOAST);
          return res;
        })
    );
  }

  pause(id: number): Promise<OneToOneSchedule> {
    return OneToOneBusinessService._updatingSchedules.run(id, () =>
      firstValueFrom(this.oneToOneAPIService.pauseSchedule(id))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.MEETING_PAUSED_TOAST);
          return res;
        })
    );
  }

  resume(id: number): Promise<OneToOneSchedule> {
    return OneToOneBusinessService._updatingSchedules.run(id, () =>
      firstValueFrom(this.oneToOneAPIService.resumeSchedule(id))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.MEETING_RESUMED_TOAST);
          return res;
        })
    );
  }

  archive(id: number): Promise<OneToOneSchedule> {
    return OneToOneBusinessService._updatingSchedules.run(id, () =>
      firstValueFrom(this.oneToOneAPIService.archiveSchedule(id))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.MEETING_ARCHIVED_TOAST);
          return res;
        })
    );
  }

  unarchive(id: number): Promise<OneToOneSchedule> {
    return OneToOneBusinessService._updatingSchedules.run(id, () =>
      firstValueFrom(this.oneToOneAPIService.unarchiveSchedule(id))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.MEETING_UNARCHIVED_TOAST);
          return res;
        })
    );
  }

  delete(id: number): Promise<OneToOneSchedule> {
    return OneToOneBusinessService._updatingSchedules.run(id, () =>
      firstValueFrom(this.oneToOneAPIService.deleteSchedule(id))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.MEETING_DELETED_TOAST);
          return res;
        })
    );
  }

  getOverviewPageData(filter: ParentFilter, paging: PagingParams, sorting: SortingParams): Observable<Page<OneToOneScheduleMinimal>> {
    return this.oneToOneAPIService.getOneToOneSchedulesForOverview(filter, paging, sorting);
  }

  getOneToOneSchedulesForManagerMe(): Observable<OneToOneScheduleDetailsView[]> {
    return this.oneToOneAPIService.getOneToOneSchedulesForManagerMe();
  }

  getTalkingPointTemplateByName(name: string): Observable<TalkingPointTemplate> {
    return this.oneToOneAPIService.getTalkingPointTemplateByName(name);
  }

  getDefaultTalkingPointTemplate(): Observable<TalkingPointTemplate> {
    return this.oneToOneAPIService.getDefaultTalkingPointTemplate();
  }

  getScheduleById(scheduleId: number): Observable<OneToOneScheduleMinimal> {
    return this.oneToOneAPIService.getOneToOneScheduleById(scheduleId);
  }

  getMeetingById(scheduleId: number, meetingId: number): Observable<OneToOneMeeting> {
    return this.oneToOneAPIService.getOneToOneMeetingById(scheduleId, meetingId);
  }

  getOneToOneScheduleDetailsById(scheduleId: number): Observable<OneToOneScheduleDetailsView> {
    return this.oneToOneAPIService.getOneToOneScheduleDetailsById(scheduleId);
  }

  getSchedulesManagedByUserId(userId: number): Observable<OneToOneScheduleDetailsView[]> {
    return this.oneToOneAPIService.getSchedulesManagedByUserId(userId);
  }

  rebuildCalendarEventsForScheduleId(id: number): Promise<OneToOneSchedule> {
    return OneToOneBusinessService._updatingSchedules.run(id, () =>
      firstValueFrom(this.oneToOneAPIService.rebuildCalendarEventsForScheduleId(id))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.CALENDAR_REBUILD_SUCCESS);
          return res;
        })
    );
  }

  rebuildCalendarEventsForUserId(userId): Promise<boolean> {
    return firstValueFrom(this.oneToOneAPIService.rebuildCalendarEventsForUserId(userId))
      .then(res => {
        this.notifyUtils.notify(OneToOneMessages.CALENDAR_REBUILD_SUCCESS);
        return res;
      });
  }

  transferScheduleToNewManager(scheduleId: number, transferOneToOneSchedule: TransferOneToOneSchedule): Promise<OneToOneSchedule> {
    return OneToOneBusinessService._updatingSchedules.run(scheduleId, () =>
      firstValueFrom(this.oneToOneAPIService.transferScheduleToNewManager(scheduleId, transferOneToOneSchedule))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.SCHEDULE_TRANSFER_SUCCESS);
          return res;
        })
    );
  }

  // TALKING POINT COMMENTS

  addTalkingPointComment(scheduleId: number, meetingId: number, talkingPointId: number, newComment: string): Promise<TalkingPoint> {
    return OneToOneBusinessService._updatingMeetings.run(meetingId, () =>
      firstValueFrom(this.oneToOneAPIService.addTalkingPointComment(scheduleId, meetingId, talkingPointId, newComment.trim()))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.ADDED_TALKING_POINT_COMMENT_TOAST);
          return res;
        })
    );
  }

  // SHARED NOTES

  addMeetingSharedNote(scheduleId: number, meetingId: number, note: string): Promise<OneToOneMeeting> {
    return OneToOneBusinessService._updatingMeetings.run(meetingId, () =>
      firstValueFrom(this.oneToOneAPIService.addMeetingSharedNotes(scheduleId, meetingId, note))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.SHARED_NOTES_UPDATE_SUCCESS);
          return res;
        })
    );
  }

  // PRIVATE NOTES

  getMeetingPrivateNotes(scheduleId: number, meetingId: number): Promise<OneToOneNoteMinimal> {
    return firstValueFrom(this.oneToOneAPIService.getMeetingPrivateNotes(scheduleId, meetingId));
  }

  addMeetingPrivateNotes(scheduleId: number, meetingId: number, note: string): Promise<OneToOneNoteMinimal> {
    return OneToOneBusinessService._updatingMeetings.run(meetingId, () =>
      firstValueFrom(this.oneToOneAPIService.addMeetingPrivateNotes(scheduleId, meetingId, note))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.PRIVATE_NOTES_UPDATE_SUCCESS);
          return res;
        })
    );
  }

  // FILES

  getMeetingFiles(scheduleId: number, meetingId: number): Observable<OneToOneMeetingFile[]> {
    return this.oneToOneAPIService.getMeetingFiles(scheduleId, meetingId);
  }

  addMeetingFile(scheduleId: number, meetingId: number, file: File): Promise<OneToOneMeetingFile> {
    return firstValueFrom(this.oneToOneAPIService.addMeetingFile(scheduleId, meetingId, file))
      .then(res => {
        this.notifyUtils.notify(OneToOneMessages.FILE_UPLOAD_SUCCESS);
        return res;
      });
  }

  deleteMeetingFile(scheduleId: number, meetingId: number, fileName: string): Promise<void> {
    return firstValueFrom(this.oneToOneAPIService.deleteMeetingFile(scheduleId, meetingId, fileName))
      .then(res => {
        this.notifyUtils.notify(OneToOneMessages.FILE_DELETE_SUCCESS);
        return res;
      });
  }

  getSidebarOneToOneSchedules(userIds: number[]): Observable<OneToOneScheduleDetailsView[]> {
    return this.oneToOneAPIService.getSidebarOneToOneSchedules(userIds);
  }

  getOneToOneSchedulesLinkedToReviews(): Observable<OneToOneScheduleDetailsView[]> {
    return this.oneToOneAPIService.getOneToOneSchedulesLinkedToReviews();
  }

  rescheduleMeeting(scheduleId: number, meetingId: number, rescheduleOneToOneMeeting: RescheduleOneToOneMeeting): Promise<OneToOneMeeting> {
    return OneToOneBusinessService._updatingMeetings.run(meetingId, () =>
      firstValueFrom(this.oneToOneAPIService.rescheduleMeeting(scheduleId, meetingId, rescheduleOneToOneMeeting))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.MEETING_RESCHEDULE_SUCCESS);
          return res;
        })
    );
  }

  updateMeetingStatus(scheduleId: number, meetingId: number, newStatus: OneToOneMeetingStatus): Promise<OneToOneMeeting> {
    return OneToOneBusinessService._updatingMeetings.run(meetingId, () =>
      firstValueFrom(this.oneToOneAPIService.updateMeetingStatus(scheduleId, meetingId, newStatus))
        .then(res => {
          const message = this.getMeetingStatusUpdateMessage(newStatus);
          this.notifyUtils.notify(message);

          return res;
        })
    );
  }

  private getMeetingStatusUpdateMessage(newStatus: OneToOneMeetingStatus): string {
    if (newStatus === OneToOneMeetingStatus.COMPLETED) {
      return OneToOneMessages.MEETING_STATUS_COMPLETED_SUCCESS;
    }

    if (newStatus === OneToOneMeetingStatus.CANCELLED) {
      return OneToOneMessages.MEETING_STATUS_CANCELLED_SUCCESS;
    }

    return OneToOneMessages.MEETING_STATUS_UPDATE_SUCCESS;
  }

  bumpOneToOneMeetingTalkingPoint(scheduleId: number, meetingId: number, talkingPointId: number): Promise<OneToOneMeeting> {
    return OneToOneBusinessService._updatingMeetings.run(meetingId, () =>
      firstValueFrom(this.oneToOneAPIService.bumpTalkingPoint(scheduleId, meetingId, talkingPointId))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.BUMPED_TALKING_POINT_TOAST);
          return res;
        })
    );
  }

  actionOneToOneMeetingTalkingPoint(scheduleId: number, meetingId: number, talkingPointId: number, checked: boolean): Promise<TalkingPoint> {
    return OneToOneBusinessService._updatingMeetings.run(meetingId, () =>
      firstValueFrom(this.oneToOneAPIService.actionTalkingPoint(scheduleId, meetingId, talkingPointId, checked))
        .then(res => {
          if (res.actioned) {
            this.notifyUtils.notify(OneToOneMessages.COMPLETED_TALKING_POINT_TOAST);
          } else {
            this.notifyUtils.notify(OneToOneMessages.UNCOMPLETED_TALKING_POINT_TOAST);
          }
          return res;
        })
    );
  }

  getMeetingSentimentScaleScores(scheduleId: number, meetingId: number): Observable<MeetingSentimentScaleScore[]> {
    return this.oneToOneAPIService.getMeetingSentimentScaleScores(scheduleId, meetingId);
  }

  updateMeetingSentimentScaleScore(scheduleId: number, meetingId: number, createDto: CreateMeetingSentimentScaleScore): Promise<MeetingSentimentScaleScore> {
    return OneToOneBusinessService._updatingMeetings.run(meetingId, () =>
      firstValueFrom(this.oneToOneAPIService.updateMeetingSentimentScaleScore(scheduleId, meetingId, createDto))
        .then(res => {
          this.notifyUtils.notify(OneToOneMessages.MEETING_SENTIMENT_SCALE_SCORE_UPDATE_SUCCESS);
          return res;
        })
    );
  }

  searchForMeetingByContents(scheduleId: number, searchArgument: string): Promise<number[]> {
    return firstValueFrom(this.oneToOneAPIService.searchForMeetingByContents(scheduleId, searchArgument));
  }

  getSentimentTimelineForSchedule(scheduleId: number): Observable<ScheduleSentimentScaleTimeline> {
    return this.oneToOneAPIService.getSentimentTimelineForSchedule(scheduleId);
  }

}
